import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  pageTitleClass,
  pageSubtitleClass,
  cardClass,
  cardHeaderClass,
  cardTitleClass,
  cardBodyClass,
  statsGridClass,
  segmentedGroupClass,
  segmentedButtonClass,
  alertErrorClass,
  spinnerClass,
  tableClass,
  tableHeadClass,
  tableHeaderCellClass,
  tableBodyClass,
  tableRowClass,
  tableCellClass,
} from "../../dashboardStyles";
import { GetPlatformOverviewAction } from "../../../../store/action";
import {
  PLAN_ORDER,
  RETIRED_PLANS,
  PlanNameKeys,
  SubscriptionPlan,
} from "../../../../constants/subscription";
import PlatformGrowthChart, {
  Delta,
  InProgressBadge,
  isCurrentMonth,
} from "./PlatformGrowthChart";

/** The windows worth offering. Anything finer than a quarter is noise here. */
const RANGES = [3, 6, 12];

const TABLE_COLUMNS = [
  { field: "newUsers", labelKey: "Dashboard.Platform.overview.table.newUsers" },
  {
    field: "newOrganizations",
    labelKey: "Dashboard.Platform.overview.table.newOrganizations",
  },
  {
    field: "newBookings",
    labelKey: "Dashboard.Platform.overview.table.newBookings",
  },
];

/**
 * The renewal figures, in the order someone chasing payments would work down.
 *
 * Each links into the Companies page pre-filtered, so a count is never a dead
 * end — the whole point of "3 expired" is being able to see which three. The
 * counts and the filter share one classifier on the server, so the number here
 * always matches the number of rows over there.
 */
const RENEWAL_ROWS = [
  {
    key: "paymentProblem",
    field: "paymentProblem",
    filter: "attention",
    tone: "text-red-600",
  },
  { key: "expired", field: "expired", filter: "attention", tone: "text-red-600" },
  {
    key: "expiringSoon",
    field: "expiringSoon",
    filter: "expiring",
    tone: "text-amber-500",
  },
  {
    key: "unbilled",
    field: "unbilled",
    filter: "unbilled",
    tone: "text-ink-300",
  },
];

const StatTile = ({ icon, value, label, hint }) => (
  <div className={cardClass}>
    <div className="flex items-center gap-4 p-5">
      <div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
        <i className={`bi ${icon} text-xl`} aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <p className="m-0 text-2xl font-bold text-ink-900">{value}</p>
        <p className="m-0 text-xs font-medium text-ink-500">{label}</p>
        {hint && <p className="m-0 mt-0.5 text-xs text-ink-400">{hint}</p>}
      </div>
    </div>
  </div>
);

StatTile.propTypes = {
  icon: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
};

/**
 * Home for platform staff: new accounts, new businesses and new bookings per
 * month, plus what each business is paying for once anyone is paying.
 *
 * A SystemOwner sees the whole platform; an Admin sees only the organizations
 * assigned to them. That narrowing happens in the API, which is why nothing on
 * this page asks who is looking — the same component renders both, and cannot
 * accidentally show one of them the other's figures.
 */
const PlatformOverview = () => {
  const { t, i18n } = useTranslation();
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // A flag, not a translated string. `t` is not a stable reference, so holding
  // the message here would put it in this callback's dependencies — and a
  // callback that changes on every render re-triggers the effect below on every
  // render, which is an infinite fetch loop rather than a slow page. The copy
  // is resolved where it is displayed instead.
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      setData(await GetPlatformOverviewAction(months));
    } catch (err) {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [months]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * "octombrie 2026" in the reader's own language. The API sends year and
   * month as numbers precisely so this decision stays here — the dashboard is
   * bilingual and the API is not.
   */
  const monthName = (point) =>
    new Date(point.year, point.month - 1, 1).toLocaleDateString(i18n.language, {
      month: "long",
      year: "numeric",
    });
  const formatNumber = (n) => n.toLocaleString(i18n.language);

  // The API sends the window oldest first, which is the order a chart reads.
  const monthly = data?.monthly ?? [];
  const newestFirst = monthly
    .map((point, i) => ({ point, previous: monthly[i - 1] ?? null }))
    .reverse();

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className={pageTitleClass}>
            {t("Dashboard.Platform.overview.title")}
          </h1>
          <p className={pageSubtitleClass}>
            {t("Dashboard.Platform.overview.subtitle")}
          </p>
        </div>

        <div className={`${segmentedGroupClass} sm:w-auto`}>
          {RANGES.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={months === value}
              onClick={() => setMonths(value)}
              className={`${segmentedButtonClass(months === value)} min-h-11`}
            >
              {t("Dashboard.Platform.overview.rangeMonths", { count: value })}
            </button>
          ))}
        </div>
      </div>

      {failed && (
        <div className={alertErrorClass} role="alert">
          <i className="bi bi-exclamation-triangle" aria-hidden="true" />
          <span>{t("Dashboard.Platform.overview.loadError")}</span>
        </div>
      )}

      {/* Only the first load gets a spinner. Changing the window keeps the
          figures already on screen, dimmed, so the page does not collapse to
          a spinner and jump back while the reader is comparing. */}
      {loading && !data ? (
        <div className="flex min-h-[40vh] items-center justify-center">
          <div
            role="status"
            aria-label={t("Dashboard.Menu.loading")}
            className={spinnerClass}
          />
        </div>
      ) : (
        data && (
          <div
            aria-busy={loading}
            className={`flex flex-col gap-6 transition-opacity ${
              loading ? "opacity-60" : ""
            }`}
          >
            <div className={statsGridClass}>
              <StatTile
                icon="bi-buildings"
                value={data.totals.organizations}
                label={t("Dashboard.Platform.overview.totals.organizations")}
                hint={t("Dashboard.Platform.overview.totals.organizationsHint", {
                  active: data.totals.activeOrganizations,
                  pending: data.totals.pendingSetupOrganizations,
                })}
              />
              <StatTile
                icon="bi-people"
                value={data.totals.users}
                label={t("Dashboard.Platform.overview.totals.users")}
              />
              <StatTile
                icon="bi-calendar-check"
                value={data.totals.bookings}
                label={t("Dashboard.Platform.overview.totals.bookings")}
              />
            </div>

            <PlatformGrowthChart monthly={monthly} />

            <div className={cardClass}>
              <div className={cardHeaderClass}>
                <h3 className={cardTitleClass}>
                  {t("Dashboard.Platform.overview.table.title")}
                </h3>
              </div>
              <div className={cardBodyClass}>
                <table className={tableClass}>
                  <thead className={tableHeadClass}>
                    <tr>
                      <th className={tableHeaderCellClass}>
                        {t("Dashboard.Platform.overview.table.month")}
                      </th>
                      {TABLE_COLUMNS.map(({ field, labelKey }) => (
                        <th
                          key={field}
                          className={`${tableHeaderCellClass} md:text-right`}
                        >
                          {t(labelKey)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  {/* Newest first: the month being asked about is almost
                      always this one or the last. Each figure is set against
                      the month before it, which in this order is the row
                      below. */}
                  <tbody className={tableBodyClass}>
                    {newestFirst.map(({ point, previous }) => {
                      const partial = isCurrentMonth(point);
                      return (
                        <tr
                          key={`${point.year}-${point.month}`}
                          className={tableRowClass}
                        >
                          <td
                            className={tableCellClass}
                            data-label={t(
                              "Dashboard.Platform.overview.table.month",
                            )}
                          >
                            <span className="inline-flex flex-wrap items-center gap-2">
                              <span className="inline-block font-medium text-ink-900 first-letter:uppercase">
                                {monthName(point)}
                              </span>
                              {partial && <InProgressBadge />}
                            </span>
                          </td>
                          {TABLE_COLUMNS.map(({ field, labelKey }) => (
                            <td
                              key={field}
                              className={`${tableCellClass} tabular-nums md:text-right`}
                              data-label={t(labelKey)}
                            >
                              <span className="inline-flex items-center gap-2">
                                {previous && !partial && (
                                  <Delta
                                    change={point[field] - previous[field]}
                                  />
                                )}
                                <span
                                  className={
                                    point[field] === 0
                                      ? "text-ink-400"
                                      : "font-medium text-ink-900"
                                  }
                                >
                                  {formatNumber(point[field])}
                                </span>
                              </span>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className={`${tableBodyClass} max-md:mt-3`}>
                    <tr className={`${tableRowClass} bg-ink-50`}>
                      <td
                        className={`${tableCellClass} border-t border-ink-200 max-md:before:hidden`}
                      >
                        <span className="font-semibold text-ink-900">
                          {t("Dashboard.Platform.overview.table.total")}
                        </span>
                      </td>
                      {TABLE_COLUMNS.map(({ field, labelKey }) => (
                        <td
                          key={field}
                          className={`${tableCellClass} border-t border-ink-200 tabular-nums md:text-right`}
                          data-label={t(labelKey)}
                        >
                          <span className="font-semibold text-ink-900">
                            {formatNumber(
                              monthly.reduce((sum, p) => sum + p[field], 0),
                            )}
                          </span>
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <div className={cardClass}>
                <div className={cardHeaderClass}>
                  <h3 className={cardTitleClass}>
                    {t("Dashboard.Platform.overview.planMix.title")}
                  </h3>
                </div>
                <div className={cardBodyClass}>
                  <div className="flex flex-col gap-3">
                    {/* Every plan, not the three on sale: this is a
                        distribution over the companies that exist, and Business
                        and Custom companies do exist. Leaving one out would drop
                        its row while still counting it in the total below, so
                        the bars would not add up. */}
                    {[
                      ...PLAN_ORDER,
                      ...RETIRED_PLANS,
                      SubscriptionPlan.CUSTOM,
                    ].map((plan) => {
                      const row = data.planMix?.find((p) => p.plan === plan);
                      const count = row?.count ?? 0;
                      const total = (data.planMix ?? []).reduce(
                        (sum, p) => sum + p.count,
                        0,
                      );
                      const pct = total === 0 ? 0 : (count / total) * 100;
                      return (
                        <div key={plan}>
                          <div className="mb-1 flex items-center justify-between text-fluid-sm">
                            <span className="text-ink-600">
                              {t(PlanNameKeys[plan])}
                            </span>
                            <span className="text-ink-500">{count}</span>
                          </div>
                          <div className="h-2 w-full overflow-hidden rounded-full bg-ink-100">
                            <div
                              className="h-full rounded-full bg-brand-500"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Money, in the only form there is any of yet.
                  Revenue itself is deliberately not a zero — nothing is charged,
                  so "0.00 €" would read as a bad month rather than as an absent
                  feature. What *is* real is who is about to lapse, so the card
                  leads with that and says plainly that the totals are missing. */}
              <div className={cardClass}>
                <div className={cardHeaderClass}>
                  <h3 className={cardTitleClass}>
                    {t("Dashboard.Platform.overview.revenue.title")}
                  </h3>
                </div>
                <div className={cardBodyClass}>
                  <div className="mb-4 flex flex-col gap-2">
                    {RENEWAL_ROWS.map(({ key, field, filter, tone }) => {
                      const count = data.renewals?.[field] ?? 0;
                      return (
                        <Link
                          key={key}
                          to={`/dashboard/companies?licence=${filter}`}
                          className="flex items-center justify-between gap-3 rounded-lg border border-ink-200 px-3 py-2 transition hover:border-brand-600 hover:bg-ink-50"
                        >
                          <span className="flex items-center gap-2 text-fluid-sm text-ink-600">
                            <i
                              className={`bi bi-circle-fill text-[0.5rem] ${tone}`}
                              aria-hidden="true"
                            />
                            {t(`Dashboard.Platform.overview.renewals.${key}`)}
                          </span>
                          <span className="font-semibold text-ink-900">
                            {count}
                          </span>
                        </Link>
                      );
                    })}
                  </div>

                  <div className="flex items-start gap-2 rounded-lg bg-ink-50 px-3 py-2.5">
                    <i
                      className="bi bi-cash-coin mt-0.5 text-ink-400"
                      aria-hidden="true"
                    />
                    <p className="m-0 text-xs text-ink-500">
                      <span className="font-semibold text-ink-600">
                        {t("Dashboard.Platform.overview.revenue.unavailable")}
                      </span>{" "}
                      {t("Dashboard.Platform.overview.revenue.hint")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default PlatformOverview;
